import { z } from 'zod';

export const MEDIA_MAX_ORIGINAL_BYTES = 50_000_000;
export const MEDIA_COMPANY_LIMIT_BYTES = 2_000_000_000;
export const MEDIA_VARIANT_ALLOWANCE_BYTES = 2_000_000;
export const MEDIA_TRASH_DAYS = 30;
export const MEDIA_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'image/heif',
] as const;
export const MEDIA_STATES = [
  'reserved',
  'uploading',
  'unknown',
  'original_stored',
  'processing',
  'ready',
  'failed',
  'held',
  'purging',
  'purged',
] as const;
export const MEDIA_PROCESSING_STATES = [
  'pending',
  'processing',
  'ready',
  'failed',
  'not_applicable',
] as const;
export const MEDIA_HOLD_CODES = [
  'PILOT_DISABLED',
  'PROOF_REQUIRED',
  'INVENTORY_REQUIRED',
  'COMPANY_STORAGE_FULL',
  'PLATFORM_STORAGE_FULL',
  'BUDGET_HOLD',
  'RATE_LIMITED',
  'UPLOAD_BUSY',
  'RECOVERY_REQUIRED',
  'PROCESSING_FAILED',
  'INVALID_PHOTO',
  'INVALID_RECEIPT',
] as const;
export type MediaHoldCode = (typeof MEDIA_HOLD_CODES)[number];
export type MediaState = (typeof MEDIA_STATES)[number];
export type MediaProcessingState = (typeof MEDIA_PROCESSING_STATES)[number];
export const mediaVariant = z.enum(['thumbnail', 'viewer', 'original']);
export type MediaVariant = z.infer<typeof mediaVariant>;
export const mediaInitiateInput = z.object({
  clientId: z.string().uuid(),
  mutationId: z.string().uuid(),
  entityType: z.enum(['job', 'visit', 'ticket', 'quote']),
  entityId: z.string().uuid(),
  filename: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[^\r\n\x00-\x1f\x7f"\\]+$/),
  contentType: z.enum(MEDIA_TYPES),
  byteSize: z.number().int().min(1).max(MEDIA_MAX_ORIGINAL_BYTES),
  sha256: z.string().regex(/^[a-f0-9]{64}$/),
  takenAt: z.string().datetime().nullable().optional(),
});
export type MediaInitiateInput = z.infer<typeof mediaInitiateInput>;
export const mediaUploadReceipt = z.object({
  uploadId: z.string().uuid(),
  attachmentId: z.string().uuid().nullable(),
  state: z.enum(MEDIA_STATES),
  durableOriginal: z.boolean(),
  processingState: z.enum(MEDIA_PROCESSING_STATES),
  holdCode: z.enum(MEDIA_HOLD_CODES).nullable(),
  retryAfterSeconds: z.number().int().positive().optional(),
});
export type MediaUploadReceipt = z.infer<typeof mediaUploadReceipt>;
export interface MediaPolicy {
  enabled: boolean;
  maxOriginalBytes: number;
  companyLimitBytes: number;
  variantAllowanceBytes: number;
  trashDays: number;
}
export interface MediaUsage {
  known: boolean;
  limitBytes: number;
  physicalBytes: number;
  reservedBytes: number;
  trashBytes: number;
  availableBytes: number;
  blockedReason: string | null;
  uploadsPaused: boolean;
}
export interface MediaTrashItem {
  id: string;
  filename: string | null;
  byteSize: number;
  deletedAt: Date;
  purgeAfter: Date;
  canRestore: boolean;
}
